import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  IconButton,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  FormLabel,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Images from '../../../Utils/images';
import '../../../styles/edit-shift-modal.scss';
import moment from 'moment-timezone';
import TimePicker from '../../../Components/TimePicker';
import { TIME_PICKER_MINUTES_STEP } from '../../../helpers/contants';
import DateInput from '../../../Components/DateInput';
import dayjs, { Dayjs } from 'dayjs';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  getShiftPreviewDataStart,
  updateShiftPreview,
} from '../../../redux/orderSlice';
import { RootState, useAppSelector } from '../../../redux/store';
import { editShiftSchema } from '../../../Utils/yupvalidations';
import { notify } from '../../../Utils/toastify';
import {
  alignShiftEndToStart,
  getCoverage,
  getEndDayTimestamp,
  getStartDayTimestamp,
  getTimeDisplay,
} from '../../../Utils/helperFns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimePickerControlled from '../../../Components/TimePickerControlled';
import WarningDialog from '../../../Components/WarningDialog';
import ToolTipComponent from '../../../Components/ToolTipComponent';
import { Worksheet } from '../../MyOrders/Components/ShiftDetails';
import { LocationOption } from '../../Cart/Components/LocationUpdateModalShift';

const TimePickerControlledAny = TimePickerControlled as any;
const TimePickerAny = TimePicker as any;
interface Shift {
  _id: string;
  actualStartTime: string;
  actualEndTime: string;
  duration: number;
  deliveryAddress: {
    locationName: string;
    locationPurpose: string[];
    locationId: string;
    setId: string;
  };
  unit?: number;
  shiftCoverage?: string;
}

interface CartDetails {
  startTime?: string;
  endTime?: string;
  totalCost?: number;
  totalHours?: number;
}

interface EditShiftModalProps {
  open: boolean;
  onClose: () => void;
  selectedWorksheet?: Worksheet;
  selectedShifts?: Shift[];
  cartDetails?: CartDetails;
  buildApiQuery: (data: any) => void;
  setSelectedDate: (date: any) => void;
}

type EditShiftFormData = yup.InferType<typeof editShiftSchema>;

// Utility function to determine shift type
const determineShiftType = (shifts: Shift[]): 'Day' | 'Night' => {
  if (!shifts || shifts.length === 0) return 'Day';

  // Check if any shift starts before 3 PM (15:00)
  const hasDayShift = shifts.some(shift => {
    const startTime = new Date(shift.actualStartTime);
    const hours = startTime.getHours();
    const minutes = startTime.getMinutes();
    const timeInMinutes = hours * 60 + minutes;
    return timeInMinutes < 15 * 60; // Before 3 PM
  });

  return hasDayShift ? 'Day' : 'Night';
};

const EditShiftModal: React.FC<any> = ({
  open,
  onClose,
  selectedWorksheet,
  selectedShifts = [],
  cartDetails,
  buildApiQuery,
  setSelectedDate,
}) => {
  // State management

  const [shiftQuantities, setShiftQuantities] = useState<{
    [key: string]: number;
  }>({});
  const [shiftTotalQuantities, setShiftTotalQuantities] = useState<{
    [key: string]: number;
  }>({});
  const [bulkEditQuantity, setBulkEditQuantity] = useState(0);
  const [isExtendServiceMode, setIsExtendServiceMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState({
    starttextField: false,
    startpickerOpen: false,
    endPickerOpen: false,
    endTextField: false,
    isStartAccepted: false,
    isEndAccepted: false,
  });
  const [timeChanged, setTimeChanged] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const { previewShiftDetails } = useAppSelector(
    (state: RootState) => state.common,
  );
  const [shiftType, setShiftType] = useState('');
  const [locationOverride, setLocationOverride] = useState<
    LocationOption | undefined
  >(undefined);

  const isAfterCurrentTime = useCallback((time: string) => {
    const currentTime = moment();
    return moment(time).isAfter(currentTime);
  }, []);

  // React Hook Form setup for edit shift
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    trigger,
    formState: { errors, isValid },
    reset,
  } = useForm<EditShiftFormData>({
    resolver: yupResolver(editShiftSchema),
    mode: 'onChange',
    defaultValues: {
      startTime: undefined,
      endTime: undefined,
      notes: '',
      extendDates: [],
    },
  });

  const {
    fields: extendDateFields,
    append: appendExtendDate,
    remove: removeExtendDate,
  } = useFieldArray({
    control,
    name: 'extendDates',
  });

  // Watch the entire extendDates array to force re-renders
  const watchedExtendDates = watch('extendDates');

  const [reRender, setReRender] = useState(0);
  // Initialize shift quantities and form values when modal opens
  const filteredShifts = useMemo(() => {
    const grouped = (selectedShifts as any[]).reduce(
      (acc: Record<string, any[]>, s: any) => {
      const key = `${s.actualStartTime || ''}_${s.actualEndTime || ''}_${
        s.deliveryAddress.locationId || ''
      }_${s.deliveryAddress.setId}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(s);
      return acc;
      },
      {} as Record<string, any[]>,
    );

    const mergedShifts = Object.values(grouped).map((group: any[]) => {
      return {
        totalUnits: group.reduce(
          (sum: number, s: any) => sum + (s.unit || 1),
          0,
        ),
        initialUnits: group.reduce(
          (sum: number, s: any) => sum + (s.unit || 1),
          0,
        ),
        isMerged: group.length > 1,
        ...group[0],
      };
    });
    if (shiftType === 'All' || shiftType === '') return [...mergedShifts];
    return mergedShifts.filter(
      (shift: any) => getCoverage(shift.actualStartTime) === shiftType,
    );
  }, [selectedShifts, shiftType]);

  // removed console logs
  useEffect(() => {
    if (filteredShifts.length > 0) {
      const initialQuantities: { [key: string]: number } = {};
      filteredShifts.forEach((shift: any) => {
        initialQuantities[shift._id] = shift.unit || 1;
        setShiftTotalQuantities((prev: any) => ({
          ...prev,
          [shift._id]: shift.totalUnits,
        }));
      });
      setShiftQuantities(initialQuantities);
    }

    // Initialize form values from cart details
    if (filteredShifts[0]?.actualStartTime) {
      setValue('startTime', dayjs(filteredShifts[0]?.actualStartTime));
    }
    if (filteredShifts[0]?.actualEndTime) {
      setValue('endTime', dayjs(filteredShifts[0]?.actualEndTime));
    }
  }, [filteredShifts, cartDetails, setValue]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setShiftQuantities({});
      setBulkEditQuantity(0);
    }
  }, [open, reset]);

  // Handle individual shift quantity change
  const handleShiftQuantityChange = (shiftId: string, increment: boolean) => {
    const shift = filteredShifts.find(s => s._id === shiftId);
    if (shift && !isFixedCost && !isAfterCurrentTime(shift.actualStartTime)) {
      return;
    }
    setShiftQuantities(prev => ({
      ...prev,
      [shiftId]: Math.max(1, prev[shiftId] + (increment ? 1 : -1)),
    }));
    setShiftTotalQuantities(prev => ({
      ...prev,
      [shiftId]: Math.max(1, prev[shiftId] + (increment ? 1 : -1)),
    }));
  };

  // Handle bulk edit quantity change
  const handleBulkEditQuantityChange = (increment: boolean) => {
    if (
      !isFixedCost &&
      filteredShifts.every(s => !isAfterCurrentTime(s.actualStartTime))
    ) {
      return;
    }
    const newBulkQuantity = increment
      ? bulkEditQuantity + 1
      : Math.max(-1, bulkEditQuantity - 1);

    setBulkEditQuantity(newBulkQuantity);

    // Update all selected shifts quantities
    const updatedQuantities = { ...shiftQuantities };
    const updatedTotalQuantities = { ...shiftTotalQuantities };
    filteredShifts.forEach(shift => {
      const editable = isFixedCost || isAfterCurrentTime(shift.actualStartTime);
      if (!editable) return;
      const currentQuantity = shiftQuantities[shift._id] || 1;
      const currentTotalQuantity = shiftTotalQuantities[shift._id] || 1;
      const newQuantity = Math.max(1, currentQuantity + (increment ? 1 : -1));
      const newTotalQuantity = Math.max(
        1,
        currentTotalQuantity + (increment ? 1 : -1),
      );
      updatedQuantities[shift._id] = newQuantity;
      updatedTotalQuantities[shift._id] = newTotalQuantity;
    });
    setShiftTotalQuantities(updatedTotalQuantities);
    setShiftQuantities(updatedQuantities);
  };

  // Time validation function (adapted from React Native)
  const onUpdateTime = (
    newTime: Date,
    timeType: 'startTime' | 'endTime',
    various?: boolean,
  ): boolean => {
    if (!filteredShifts?.length) return false;

    // Special handling for end time AM/PM logic - adjust time before validation
    const adjustedTime = newTime;
    const currentStartRaw = getValues('startTime');
    const currentEndRaw = getValues('endTime');

    // Narrow input types safely
    const currentStart =
      currentStartRaw &&
      (typeof currentStartRaw === 'string' ||
        currentStartRaw instanceof Date ||
        dayjs.isDayjs(currentStartRaw))
        ? dayjs(currentStartRaw)
        : null;

    const currentEnd =
      currentEndRaw &&
      (typeof currentEndRaw === 'string' ||
        currentEndRaw instanceof Date ||
        dayjs.isDayjs(currentEndRaw))
        ? dayjs(currentEndRaw)
        : null;

    const newTimeDayjs = dayjs(newTime);

    const otherTime = timeType === 'startTime' ? currentEnd : currentStart;

    const startFull =
      timeType === 'startTime'
        ? newTimeDayjs
        : currentStart
          ? currentStart
          : null;
    const endInput =
      timeType === 'endTime'
        ? newTimeDayjs
        : otherTime
          ? dayjs(otherTime)
          : null;

    // ---- Duration rule (max 16h unless Swing Shift) ----
    if (startFull && endInput) {
      const endAligned = alignShiftEndToStart(startFull, endInput);
      const durationMins = endAligned.diff(startFull, 'minute');
      const durationHrs = durationMins / 60;

      if (durationHrs > 16 && !various) {
        notify(
          `Shift duration (${durationHrs.toFixed(1)}h) exceeds 16 hour limit`,
          'error',
        );
        return false;
      }
    }
    const currentShiftType = determineShiftType(filteredShifts);
    const newHours = adjustedTime.getHours();
    const newMinutes = adjustedTime.getMinutes();
    const newTimeInMinutes = newHours * 60 + newMinutes;
    const dayStartCutoff = 15 * 60; // 3 PM
    const nightStartCutoff = 15 * 60; // 3 PM

    // if (currentShiftType === 'Day') {
    //   if (timeType === 'startTime' && newTimeInMinutes >= dayStartCutoff) {
    //     notify('Day shifts must start before 3 PM', 'error');
    //     setValue('startTime', dayjs(filteredShifts[0]?.actualStartTime));
    //     return false;
    //   }
    // }

    // if (currentShiftType === 'Night') {
    //   if (timeType === 'startTime' && newTimeInMinutes < nightStartCutoff) {
    //     notify('Night shifts must start at or after 3 PM', 'error');
    //     return false;
    //   }
    // }

    // If all validations pass, update the form value
    if (timeType === 'startTime') {
      setValue('startTime', newTimeDayjs);
      if (currentEnd) {
        setValue('endTime', alignShiftEndToStart(newTimeDayjs, currentEnd));
      }
    } else if (startFull) {
      setValue('endTime', alignShiftEndToStart(startFull, newTimeDayjs));
    } else {
      setValue('endTime', newTimeDayjs);
    }

    return true;
  };

  // Handle form submission
  const onSubmit = (data: EditShiftFormData) => {
    getTimeDisplay(filteredShifts || [], false);
    setIsSubmitting(true);
    const startOffset = data?.startTime
      ? dayjs(data.startTime as any).toDate().getTimezoneOffset()
      : new Date().getTimezoneOffset();
    const endOffset = data?.endTime
      ? dayjs(data.endTime as any).toDate().getTimezoneOffset()
      : startOffset;

    const payload = {
      offset: startOffset,
      startOffset,
      endOffset,
      editItems: [
        {
          shifts: selectedShifts.map(shift => ({
            shiftId: shift._id,
            updatedUnits: shiftQuantities[shift._id] || 1,
          })),
          oldSetId: '669f5bcf18aad9a01676c003',
          newSetId: '669f5bcf18aad9a01676c003',
          productionId: '6672a465b31b1c69202566a7',
          productionName: '1st Production House updated',
          productionType: 'Commercial',
          newSetName: '1st Set of Production updated',
          cartId: selectedWorksheet?.cartId || '',
          ...(timeChanged && {
            updatedStartTime: dayjs(data?.startTime as any).valueOf(),
            updatedEndTime: dayjs(data?.endTime as any).valueOf(),
          }),
          ...(data.notes && {
            notes: {
              note: data.notes,
              noteFor: 'PUBLIC',
            },
          }),
          ...(locationOverride && {
            updatedLocations: selectedShifts.map(shift => ({
              oldLocationId: shift.deliveryAddress?.locationId,
              newLocationId: locationOverride.value,
            })),
          }),
          ...(data?.extendDates?.length &&
            data?.extendDates?.length > 0 && {
              extendService: {
                isExtendService: true,
                dateRanges: data?.extendDates?.map(field => ({
                  startTime: dayjs(field.startDate as any)
                    .startOf('day')
                    .valueOf(), // 00:00:00 start of day timestamp
                  endTime: dayjs(field.endDate as any)
                    .endOf('day')
                    .valueOf(), // 00:00:00 of endDate day, no extra time added
                })),
              },
            }),
        },
      ],
    };

    // TODO: Call API with payload

    updateShiftPreview(
      payload,
      'fromPreview',
      () => {
        // removed console logs
        const extendedItem = payload.editItems.find(item => item.extendService);

        const cartId =
          previewShiftDetails?.cart[0]?.groupedProducts[0]?.products[0]?.cartId;
        let fromDate = getStartDayTimestamp(cartDetails?.startTime);
        let toDate = getEndDayTimestamp(cartDetails?.endTime);

        if (extendedItem?.extendService?.dateRanges?.length) {
          const extStart = getStartDayTimestamp(
            extendedItem.extendService?.dateRanges?.[0]?.startTime,
          );
          const extEnd = getEndDayTimestamp(
            extendedItem.extendService?.dateRanges?.[0]?.endTime,
          );

          fromDate = extStart && extStart < fromDate ? extStart : fromDate;
          toDate = extEnd && extEnd > toDate ? extEnd : toDate;
        }
        // removed console logs
        const query = `limit=10&cartIds=${cartId}&pageNo=1&toDate=${toDate}&fromDate=${fromDate}`;
        getShiftPreviewDataStart(query);
        setSelectedDate(null);
        // navigate('/preview-shift', { state: state, replace: true });
        handleClose();
        setIsSubmitting(false);
      },
      () => {
        setIsSubmitting(false);
      },
    );
  };

  const handleClose = () => {
    setPickerOpen({
      endPickerOpen: false,
      startpickerOpen: false,
      endTextField: false,
      starttextField: false,
      isEndAccepted: false,
      isStartAccepted: false,
    });
    onClose();
    setWarningOpen(false);
    setShiftType('');
  };
  // Handle extend service
  const handleExtendService = () => {
    setIsExtendServiceMode(true);

    if (extendDateFields.length < 1) {
      appendExtendDate({
        startDate: dayjs(cartDetails?.endTime).add(1, 'day') as any,
        endDate: undefined as any,
      });
      void trigger('extendDates');
    }
  };

  const validateExtendDate = (
    date: Dayjs | null,
    type: 'startDate' | 'endDate',
    idx: number,
    setValue: any,
    extendFields: Array<{ startDate?: any; endDate?: any }>,
    // pass cartDetails.endTime as Dayjs here
  ): boolean => {
    if (!date) return true;

    // Normalize to Dayjs if native Date
    const valDate = dayjs.isDayjs(date) ? date : dayjs(date);
    const originalEndDate = dayjs(cartDetails?.endTime);
    const originalStartDate = dayjs(cartDetails?.startTime);
    const today = dayjs().startOf('day');
    if (valDate.isBefore(today, 'day')) {
      notify('Date cannot be before today', 'error');
      return false;
    }

    // Determine other boundary for the current range
    const newStart =
      type === 'startDate'
        ? valDate
        : extendFields[idx]?.startDate
        ? dayjs(extendFields[idx].startDate)
        : undefined;

    const newEnd =
      type === 'endDate'
        ? valDate
        : extendFields[idx]?.endDate
        ? dayjs(extendFields[idx].endDate)
        : undefined;

    // Check that end >= start if both exist
    if (newStart && newEnd && newEnd.isBefore(newStart, 'day')) {
      notify('End date cannot be before start date', 'error');
      return false;
    }

    // Check overlap against original end date (cannot extend inside existing original period)
    if (
      (newStart &&
        (newStart.isBefore(originalEndDate, 'day') ||
          newStart.isSame(originalEndDate, 'day')) &&
        !newStart.isBefore(originalStartDate, 'day')) ||
      (newEnd &&
        (newEnd.isAfter(originalStartDate, 'day') ||
          newEnd.isSame(originalStartDate, 'day')) &&
        !newEnd.isBefore(originalStartDate, 'day') &&
        !newEnd.isAfter(originalEndDate, 'day'))
    ) {
      notify('Date cannot be between start and end date', 'error');
      return false;
    }

    if (
      newStart &&
      newStart.isBefore(originalStartDate, 'day') &&
      newEnd &&
      newEnd.isAfter(originalEndDate, 'day')
    ) {
      notify('End date cannot be after service start date.', 'error');
      return false;
    }
    // Check overlap against other extended ranges
    for (let i = 0; i < extendFields.length; i++) {
      if (i === idx) continue;

      const ext = extendFields[i];
      if (!ext.startDate || !ext.endDate) continue;

      const extStart = dayjs(ext.startDate);
      const extEnd = dayjs(ext.endDate);

      if (newStart && newEnd) {
        // Overlap if ranges intersect or touch
        const isOverlap =
          // newEnd.isBefore(extStart, 'day') || newStart.isAfter(extEnd, 'day')
          newEnd.isBefore(extEnd, 'day') || newEnd.isSame(extEnd, 'day');
        if (isOverlap) {
          notify('Date cannot be between start and end date', 'error');
          return false;
        }
      } else if (newStart) {
        // newStart must not lie inside any existing extended range (inclusive)
        if (
          newStart.isSame(extStart, 'day') ||
          newStart.isSame(extEnd, 'day') ||
          (newStart.isAfter(extStart, 'day') &&
            newStart.isBefore(extEnd, 'day'))
        ) {
          notify('Date cannot be between start and end date', 'error');
          return false;
        }
      } else if (newEnd) {
        // newEnd must not lie inside any existing extended range (inclusive)
        if (
          newEnd.isSame(extEnd, 'day') ||
          (newEnd.isBefore(extEnd, 'day') && newEnd.isAfter(extStart, 'day'))
        ) {
          notify('Date cannot be between start and end date', 'error');
          return false;
        }
      }
    }

    return true;
  };

  // Handle extend service form submission
  const onExtendServiceSubmit = (data: EditShiftFormData) => {
    // removed console logs
    // TODO: Implement extend service API call
    setIsExtendServiceMode(false);
    reset();
  };

  // Handle back to edit mode
  const handleBackToEdit = () => {
    setIsExtendServiceMode(false);

    setValue('extendDates', [], { shouldValidate: true });
  };

  // Watch for start date changes and clear end date if needed
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.startsWith('extendDates.') && name.endsWith('.startDate')) {
        const index = parseInt(name.split('.')[1]);
        const startDate = value.extendDates?.[index]?.startDate;
        const endDate = value.extendDates?.[index]?.endDate;

        // If start date is after current end date, clear end date
        if (
          startDate &&
          endDate &&
          dayjs(startDate as any).isAfter(dayjs(endDate as any))
        ) {
          setValue(`extendDates.${index}.endDate`, undefined as any);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  // Handle location purpose from override
  // Find worksheet location details
  const worksheetLocation = selectedWorksheet?.shifts?.find(
    (val: any) => val?.locationDetails?._id === selectedWorksheet?.locationId,
  )?.locationDetails;

  const worksheetPurposes = worksheetLocation?.locationPurpose || [];

  // Determine which purposes to show
  const purposesToShow =
    (locationOverride?.locationPurpose?.length ?? 0) > 0
      ? locationOverride?.locationPurpose || []
      : worksheetPurposes;
  const remainingCount =
    purposesToShow.length > 1 ? purposesToShow.length - 1 : 0;

  // Location label and address (match MyOrders format)
  const locationLabel =
    locationOverride?.label || selectedWorksheet?.locationName || '';
  const locationAddress =
    locationOverride?.address || selectedWorksheet?.address || '';
  const locationDisplay = `${locationLabel} - ${purposesToShow[0] || ''} `;

  if (!selectedWorksheet) return null;
  const showTimeColumn = !selectedWorksheet?.rentPricing?.isFlexibleShiftHr;
  const isFixedCost = !!selectedWorksheet?.rentPricing?.isFlexibleShiftHr;
  const hasAnyFutureShift = filteredShifts.some(s =>
    isAfterCurrentTime(s.actualStartTime),
  );
  return (
    <Dialog open={open} onClose={handleClose} fullWidth className="modal-main edit-shift-modal">
      <div className="modal-dialog md-sm">
        <div className="modal-content">
          <div className="modal-title d-flex align-center flex-between">
            <Typography variant="h6" fontWeight={600}>
              Edit Shift
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body pt-20">
              <div className="body-scroll-sm">
                {/* Service Information */}
                <div className="card-grid border">
                  <img
                    src={
                      selectedWorksheet.productInfo?.thumbnailImg ||
                      Images.AUTH_BG_1
                    }
                    alt={selectedWorksheet.productInfo?.title}
                  />
                  <div className="pl-12">
                    <h2 className="fw600 MuiTypography-subtitle1 mb-8">
                      {selectedWorksheet.productInfo?.title}
                    </h2>
                    {/* <div className="d-flex">
                      <i className="pin-icon mr-4"></i>
                      <p className="MuiTypography-subtitle text-text">
                        {selectedWorksheet.locationName}
                      </p>
                    </div> */}
                  </div>
                </div>

                <div className="card-grid">
                  <span className="icon-round blue border">
                    <i className="set-white-icon"></i>
                  </span>
                  <div className="pl-12 d-flex flex-between">
                    <div>
                      <div className="d-flex mb-8 flex-between">
                        <p className="MuiTypography-body2 fw600">
                          {selectedWorksheet?.setName?.toUpperCase()}
                        </p>
                      </div>
                      <p className="MuiTypography-subtitle1 text-text">
                        {purposesToShow?.[0]}
                        {remainingCount > 0 && (
                          <ToolTipComponent
                            theme="dark"
                            title={
                              <div>
                                {purposesToShow.map(
                                  (purpose: string, index: number) => {
                                    if (index == 0) return null;
                                    return <div key={index}>{purpose}</div>;
                                  },
                                )}
                              </div>
                            }
                          >
                            <span className="cursor-pointer">
                              {' '}
                              + {remainingCount}
                            </span>
                          </ToolTipComponent>
                        )}{' '}
                        : {locationLabel + ', ' + locationAddress}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Shift Type Selection */}
                {/* {showTimeColumn && <div className="mb-24 pt-10 border-bottom">
                  <p className="MuiTypography-subtitle1 mb-8 fw500">
                    Shift Type
                  </p>
                  <RadioGroup
                    value={
                      shiftType
                        ? shiftType
                        : new Set(
                          selectedShifts.map(s =>
                            getCoverage(s.actualStartTime),
                          ),
                        ).size > 1
                          ? 'All'
                          : getCoverage(selectedShifts[0].actualStartTime)
                    }
                    onChange={e => setShiftType(e.target.value)}
                    row
                    className="radio-regular"
                  >
                    <FormControlLabel
                      value="Day"
                      control={<Radio />}
                      label="Shift - Day"
                      disabled={
                        new Set(
                          selectedShifts.map(s =>
                            getCoverage(s.actualStartTime),
                          ),
                        ).size == 1
                      }
                    />
                    <FormControlLabel
                      value="Night"
                      control={<Radio />}
                      label="Shift - Night"
                      disabled={
                        new Set(
                          selectedShifts.map(s =>
                            getCoverage(s.actualStartTime),
                          ),
                        ).size == 1
                      }
                    />
                    <FormControlLabel
                      value="All"
                      control={<Radio />}
                      label="All Shift"
                      disabled={
                        new Set(
                          selectedShifts.map(s =>
                            getCoverage(s.actualStartTime),
                          ),
                        ).size == 1
                      }
                    />
                  </RadioGroup>
                </div>} */}

                {filteredShifts.length > 1 && (
                  <div className="shift-bg-blue lg mb-15">
                    <p className="MuiTypography-subtitle1 mb-4">
                      Bulk Edit Quantity
                    </p>

                    <Grid container spacing={1}>
                      <Grid item xs={10}>
                        <p className="MuiTypography-subtitle3 text-text lh-14">
                          Adjusting this value will uniformly increase or
                          decrease all the dates listed below.This means every
                          quantity on each date will shift forward or backward
                          by the same amount.
                        </p>
                      </Grid>
                      <Grid item xs={2}>
                        <div className="quantity-shift">
                          <IconButton
                            className="blank-btn"
                            onClick={e => {
                              e.preventDefault();
                              handleBulkEditQuantityChange(false);
                            }}
                            disabled={
                              bulkEditQuantity === 0 ||
                              (!isFixedCost && !hasAnyFutureShift)
                            }
                            type="button"
                          >
                            <i className="minus-icon"></i>
                          </IconButton>
                          <p>{bulkEditQuantity}</p>
                          <IconButton
                            className="blank-btn"
                            onClick={() => handleBulkEditQuantityChange(true)}
                            type="button"
                            disabled={!isFixedCost && !hasAnyFutureShift}
                          >
                            <i className="plus-icon"></i>
                          </IconButton>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                )}

                {/* Shift Schedule List */}
                <div className="mb-24 border-bottom">
                  <p className="MuiTypography-subtitle1 mb-8 fw500">
                    Shift Schedule
                  </p>
                  <div className="shift-grid">
                    {/* Header */}
                    <Grid container spacing={1} className="mb-8">
                      <Grid item xs={showTimeColumn ? 2.5 : 3.5}>
                        <p className="MuiTypography-subtitle1 text-text">
                          Date
                        </p>
                      </Grid>

                      {showTimeColumn && (
                        <Grid item xs={4}>
                          <p className="MuiTypography-subtitle1 text-text">
                            Time Range
                          </p>
                        </Grid>
                      )}

                      <Grid item xs={showTimeColumn ? 4 : 6}>
                        <p className="MuiTypography-subtitle1 text-text">
                          Location
                        </p>
                      </Grid>

                      <Grid item xs={1.5}>
                        <p className="MuiTypography-subtitle1 text-text">Qty</p>
                      </Grid>
                    </Grid>

                    {/* Rows */}
                    {filteredShifts.map((shift, index) =>
                      (() => {
                        const isPast = dayjs(
                          shift.actualStartTime,
                        ).isSameOrBefore(dayjs());
                        const disablePastEdits = !isFixedCost && isPast;
                        const rowPurposes =
                          (locationOverride?.locationPurpose?.length ?? 0) > 0
                            ? locationOverride?.locationPurpose || []
                            : worksheetPurposes;
                        const rowLocationName =
                          locationOverride?.label ||
                          shift.deliveryAddress?.locationName;
                        return (
                          <Grid
                            container
                            spacing={1}
                            key={shift._id}
                            className="align-unset mb-12"
                          >
                            {/* DATE */}
                            <Grid item xs={showTimeColumn ? 2.6 : 3.6}>
                              <p className="MuiTypography-subtitle1 text-primary_blue_500 fw400 shift-bg-blue align-center d-flex">
                                {moment(shift.actualStartTime).format(
                                  'ddd, MMM D',
                                )}
                              </p>
                            </Grid>

                            {/* TIME (Conditional) */}
                            {showTimeColumn && (
                              <Grid item xs={4.5}>
                                <div className="d-flex shift-bg-blue align-center">
                                  <p className="MuiTypography-subtitle1 text-primary_blue_500">
                                    {dayjs(shift.actualStartTime).format(
                                      'h:mm A',
                                    )}
                                    {' - '}
                                    {dayjs(shift.actualEndTime).format(
                                      'h:mm A',
                                    )}{' '}
                                    ({getCoverage(shift.actualStartTime)})
                                  </p>
                                </div>
                              </Grid>
                            )}

                            {/* LOCATION */}
                            <Grid item xs={showTimeColumn ? 2.9 : 6}>
                              <div className="d-flex shift-bg-blue align-center">
                                <i className="pin-icon mr-6"></i>

                                <ToolTipComponent
                                  theme="dark"
                                  title={`${rowLocationName}${
                                    rowPurposes.length > 0
                                      ? ' - ' + rowPurposes.join(', ')
                                      : ''
                                  }`}
                                >
                                  <p className="MuiTypography-body2 text-gray_900 trunc">
                                    <span>
                                      {rowLocationName}
                                      {rowPurposes.length > 0
                                        ? ' - ' + rowPurposes.join(', ')
                                        : ''}
                                    </span>
                                  </p>
                                </ToolTipComponent>
                              </div>
                            </Grid>

                            {/* QTY */}
                            <Grid item xs={1.5}>
                              <div className="quantity-shift">
                                <IconButton
                                  className="blank-btn"
                                  type="button"
                                  onClick={() =>
                                    handleShiftQuantityChange(shift._id, false)
                                  }
                                  disabled={
                                    disablePastEdits ||
                                    shiftTotalQuantities[shift._id] ===
                                      shift.initialUnits
                                  }
                                >
                                  <i className="minus-icon"></i>
                                </IconButton>

                                <p>{shiftTotalQuantities[shift._id] || 1}</p>

                                <IconButton
                                  onClick={() =>
                                    handleShiftQuantityChange(shift._id, true)
                                  }
                                  className="blank-btn"
                                  disabled={disablePastEdits}
                                >
                                  <i className="plus-icon"></i>
                                </IconButton>
                              </div>
                            </Grid>
                          </Grid>
                        );
                      })(),
                    )}
                  </div>
                  {/* <Typography
                    variant="body2"
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      mt: 1,
                      textDecoration: 'underline',
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    View Notes
                  </Typography> */}
                  {selectedWorksheet?.rentPricing?.isFlexibleShiftHr &&
                    selectedWorksheet?.rentPricing?.minRateModel ==
                      'WEEKLY' && (
                      <div
                        className="grey-box yellow d-flex align-start mb-24"
                        style={{
                          border: '1px solid var(--warning_500)',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      >
                        <i
                          className="info-icon mr-6"
                          style={{
                            flexShrink: 0,
                            filter:
                              'brightness(0) saturate(100%) invert(33%) sepia(26%) saturate(3764%) hue-rotate(358deg) brightness(90%) contrast(96%)',
                          }}
                        />
                        <p className="MuiTypography-subtitle1 mb-0 lh-14">
                          Are you sure? Increasing the quantity on a single day
                          will result in an additional week charge. To add a
                          single unit only, add a new service at the day rate.
                        </p>
                      </div>
                    )}
                </div>

                {/* Time Details */}
                <div className="mb-16">
                  {!selectedWorksheet?.rentPricing?.isFlexibleShiftHr && (
                    <Grid container spacing={1}>
                      <Grid item md={6} xs={12}>
                        <div className="form-in lg">
                          <Controller
                            name="startTime"
                            control={control}
                            render={({ field }) => {
                              const startVaries = getTimeDisplay(
                                filteredShifts,
                                true,
                              ).startVaries;
                              const storedStartTime = watch(`startTime`);
                              const oldVal = storedStartTime;
                              const allCoverage = filteredShifts.map(val =>
                                getCoverage(val.actualStartTime),
                              );
                              const disabled =
                                allCoverage?.length > 1
                                  ? new Set(allCoverage).size > 1
                                  : false;
                              if (startVaries) {
                                if (!pickerOpen.starttextField) {
                                  return (
                                    <FormControl>
                                      <FormLabel className="formLabel">
                                        Start Time <sup>*</sup>
                                      </FormLabel>
                                      <TextField
                                        value="Various"
                                        disabled={disabled}
                                        InputProps={{
                                          readOnly: true,
                                          endAdornment: (
                                            <div className="inputEndIcon">
                                              <IconButton
                                                disabled={disabled}
                                                onClick={e => {
                                                  if (disabled) {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    return;
                                                  }
                                                  setPickerOpen(prev => ({
                                                    ...prev,
                                                    startpickerOpen: true,
                                                    starttextField: true,
                                                  }));
                                                }}
                                              >
                                                <AccessTimeIcon fontSize="small" />
                                              </IconButton>
                                            </div>
                                          ),
                                        }}
                                        onClick={() => {
                                          if (disabled) return;
                                          setPickerOpen(prev => ({
                                            ...prev,
                                            startpickerOpen: true, // startPickerOpen: true,
                                            starttextField: true, //      endTextField: true,
                                          }));
                                        }}
                                        fullWidth
                                      />
                                    </FormControl>
                                  );
                                }
                                return (
                                  <TimePickerControlledAny
                                    control={control}
                                    disabled={disabled}
                                    key={`start-time-${reRender}`}
                                    id="start-time"
                                    open={pickerOpen.startpickerOpen}
                                    onClose={() => {
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        startpickerOpen: false,
                                        starttextField: prev.isStartAccepted
                                          ? true
                                          : false,
                                      }));
                                    }}
                                    date={watch('startTime')}
                                    onClick={() =>
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        startpickerOpen: true,
                                      }))
                                    }
                                    minutesStep={TIME_PICKER_MINUTES_STEP}
                                    label="Start Time"
                                    name="startTime"
                                    placeholder={'Select Start Time'}
                                    error={!!errors.startTime}
                                    helperText={errors.startTime?.message}
                                    requiredField
                                    validate
                                    value={
                                      pickerOpen.isStartAccepted
                                        ? watch('startTime')
                                        : null
                                    }
                                    onChange={(val: any) => {
                                      if (val && val.toDate) {
                                        const newTime = val.toDate();
                                        // removed console logs
                                        if (
                                          onUpdateTime(
                                            newTime,
                                            'startTime',
                                            true,
                                          )
                                        ) {
                                          setValue('startTime', val);
                                        } else {
                                          setValue(
                                            'startTime',
                                            watch('startTime'),
                                          );
                                          setReRender(prev => prev + 1);
                                        }
                                      } else {
                                        setValue(
                                          'startTime',
                                          watch('startTime'),
                                        );
                                        setReRender(prev => prev + 1);
                                      }

                                      setPickerOpen(prev => ({
                                        ...prev,
                                        isStartAccepted: true,
                                      }));
                                      setTimeChanged(true);
                                    }}
                                  />
                                );
                              } else {
                                return (
                                  <TimePickerAny
                                    control={control}
                                    key={`start-time-${reRender}`}
                                    id="start-time"
                                    disabled={disabled}
                                    minutesStep={TIME_PICKER_MINUTES_STEP}
                                    label="Start Time"
                                    name="startTime"
                                    placeholder={'Select Start Time'}
                                    error={!!errors.startTime}
                                    helperText={errors.startTime?.message}
                                    requiredField
                                    validate
                                    value={watch('startTime')}
                                    onChange={(val: any) => {
                                      if (val && val.toDate) {
                                        const newTime = val.toDate();
                                        if (
                                          onUpdateTime(newTime, 'startTime')
                                        ) {
                                          setValue('startTime', val);
                                        } else {
                                          setValue(
                                            'startTime',
                                            watch('startTime'),
                                          );
                                          setReRender(prev => prev + 1);
                                        }
                                      } else {
                                        setValue(
                                          'startTime',
                                          watch('startTime'),
                                        );
                                        setReRender(prev => prev + 1);
                                      }
                                      setTimeChanged(true);
                                    }}
                                  />
                                );
                              }
                            }}
                          />
                        </div>
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <div className="form-in lg">
                          <Controller
                            name="endTime"
                            control={control}
                            render={({ field }) => {
                              const endVaries = getTimeDisplay(
                                filteredShifts,
                                true,
                              ).endVaries;
                              const storedStartTime = watch(`endTime`);
                              const oldVal = storedStartTime;
                              const allCoverage = filteredShifts.map(val =>
                                getCoverage(val.actualStartTime),
                              );
                              const disabled =
                                allCoverage?.length > 1
                                  ? new Set(allCoverage).size > 1
                                  : false;
                              if (endVaries) {
                                if (!pickerOpen.endTextField) {
                                  return (
                                    <FormControl>
                                      <FormLabel className="formLabel">
                                        End Time <sup>*</sup>
                                      </FormLabel>
                                      <TextField
                                        value="Various"
                                        disabled={disabled}
                                        InputProps={{
                                          readOnly: true,
                                          endAdornment: (
                                            <div className="inputEndIcon">
                                              <IconButton
                                                onClick={() =>
                                                  setPickerOpen(prev => ({
                                                    ...prev,
                                                    endPickerOpen: true,
                                                    endTextField: true,
                                                  }))
                                                }
                                                disabled={disabled}
                                              >
                                                <AccessTimeIcon fontSize="small" />
                                              </IconButton>
                                            </div>
                                          ),
                                        }}
                                        onClick={() => {
                                          if (disabled) return;
                                          setPickerOpen(prev => ({
                                            ...prev,
                                            endPickerOpen: true,
                                            endTextField: true,
                                          }));
                                        }}
                                        fullWidth
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </FormControl>
                                  );
                                }
                                return (
                                  <TimePickerControlledAny
                                    control={control}
                                    key={`end-time-${reRender}`}
                                    id="end-time"
                                    open={pickerOpen.endPickerOpen}
                                    onClose={() => {
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        endPickerOpen: false,
                                        endTextField: prev.isEndAccepted
                                          ? true
                                          : false,
                                      }));
                                    }}
                                    onClick={() =>
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        endPickerOpen: true,
                                      }))
                                    }
                                    date={watch('endTime')}
                                    disabled={disabled}
                                    minutesStep={TIME_PICKER_MINUTES_STEP}
                                    label="End Time"
                                    name="endTime"
                                    placeholder={'Select End Time'}
                                    error={!!errors.startTime}
                                    helperText={errors.startTime?.message}
                                    requiredField
                                    validate
                                    value={
                                      pickerOpen.isStartAccepted
                                        ? watch('endTime')
                                        : null
                                    }
                                    onChange={(val: any) => {
                                      if (val && val.toDate) {
                                        const newTime = val.toDate();

                                        if (
                                          onUpdateTime(newTime, 'endTime', true)
                                        ) {
                                          setReRender(prev => prev + 1);
                                        } else {
                                          setValue('endTime', watch('endTime'));
                                          setReRender(prev => prev + 1);
                                        }
                                      } else {
                                        setValue('endTime', watch('endTime'));
                                        setReRender(prev => prev + 1);
                                      }
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        isEndAccepted: true,
                                      }));
                                      setTimeChanged(true);
                                    }}
                                  />
                                );
                              } else {
                                return (
                                  <TimePickerAny
                                    control={control}
                                    key={`end-time-${reRender}`}
                                    id="end-time"
                                    minutesStep={TIME_PICKER_MINUTES_STEP}
                                    disabled={disabled}
                                    label="End Time"
                                    name="endTime"
                                    placeholder={'Select End Time'}
                                    error={!!errors.startTime}
                                    helperText={errors.startTime?.message}
                                    requiredField
                                    validate
                                    value={watch('endTime')}
                                    onChange={(val: any) => {
                                      if (val && val.toDate) {
                                        const newTime = val.toDate();
                                        if (onUpdateTime(newTime, 'endTime')) {
                                          setReRender(prev => prev + 1);
                                        } else {
                                          setValue('endTime', watch('endTime'));
                                          setReRender(prev => prev + 1);
                                        }
                                      } else {
                                        setValue('endTime', watch('endTime'));
                                        setReRender(prev => prev + 1);
                                      }
                                      setTimeChanged(true);
                                    }}
                                  />
                                );
                              }
                            }}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  )}
                  {(getTimeDisplay(filteredShifts || [], true).startVaries ||
                    getTimeDisplay(filteredShifts || [], true).endVaries) && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Various times selected. Editing the time will overwrite
                      the time.
                    </Typography>
                  )}
                </div>

                {extendDateFields.length === 0 && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleExtendService}
                    className="lg-height sm-width mb-16"
                  >
                    Extend Service
                  </Button>
                )}
                {/* Extend Service Button */}
                {extendDateFields.length > 0 && (
                  <div className="d-flex align-center flex-between">
                    <div>
                      <p className="MuiTypography-body2 fw600">
                        Extend Service
                      </p>
                    </div>

                    <div>
                      <Button
                        variant="text"
                        size="medium"
                        onClick={handleBackToEdit}
                      >
                        <u>Cancel</u>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Extend Service Form */}
                {isExtendServiceMode && (
                  <>
                    {/* Extend Dates Array */}
                    <div className="mb-16">
                      {extendDateFields?.map((field, index) => (
                        <>
                          {index >= 1 && (
                            <p className="MuiTypography-subtitle1 text-text fw500 mt-16">
                              Service Range {index + 1}
                            </p>
                          )}
                          <Grid
                            container
                            spacing={1.5}
                            key={`${field.id}-${reRender}`}
                          >
                            <Grid item sm={5.3} xs={12}>
                              <div className="form-in lg">
                                <DateInput
                                  value={
                                    watch(`extendDates.${index}.startDate`) ||
                                    dayjs(cartDetails?.endTime).add(1, 'day') ||
                                    new Date()
                                  }
                                  requiredField
                                  disabled={false}
                                  label="Select Start Date"
                                  placeholder="Start Date"
                                  name={`extendDates.${index}.startDate`}
                                  formProps={{ control }}
                                  error={
                                    !!errors?.extendDates?.[index]?.startDate
                                  }
                                  validate
                                  formatDate
                                  format={'MMM DD, YYYY'}
                                  helperText={
                                    !!errors?.extendDates?.[index]
                                      ?.startDate && (
                                      <p className="helper-error">
                                        {
                                          errors.extendDates[index]?.startDate
                                            ?.message
                                        }
                                      </p>
                                    )
                                  }
                                  onChange={(val: any) => {
                                    if (!val) return;
                                    const extendFields =
                                      watch('extendDates') || [];
                                    const prevValue = watch(
                                      `extendDates.${index}.startDate`,
                                    );

                                    const isValid = validateExtendDate(
                                      val,
                                      'startDate',
                                      index,
                                      setValue as (
                                        name: string,
                                        value: any,
                                        options?: any,
                                      ) => void,
                                      extendFields as unknown as Array<{
                                        startDate?: Dayjs | null;
                                        endDate?: Dayjs | null;
                                      }>,
                                    );

                                    if (isValid) {
                                      setValue(
                                        `extendDates.${index}.startDate`,
                                        val,
                                        { shouldDirty: true },
                                      );
                                    } else {
                                      setValue(
                                        `extendDates.${index}.startDate`,
                                        prevValue,
                                        { shouldDirty: true },
                                      );
                                      setReRender(prev => prev + 1);
                                    }
                                  }}
                                />
                              </div>
                            </Grid>

                            <Grid item sm={5.3} xs={10}>
                              <div className="form-in lg">
                                <DateInput
                                  minDate={
                                    watchedExtendDates?.[index]?.startDate ||
                                    new Date()
                                  }
                                  value={
                                    watch(`extendDates.${index}.endDate`) ||
                                    null
                                  }
                                  requiredField
                                  disabled={false}
                                  label="Select End Date"
                                  placeholder="End Date"
                                  name={`extendDates.${index}.endDate`}
                                  formProps={{ control }}
                                  error={
                                    !!errors?.extendDates?.[index]?.endDate
                                  }
                                  validate
                                  formatDate
                                  format={'MMM DD, YYYY'}
                                  helperText={
                                    !!errors?.extendDates?.[index]?.endDate && (
                                      <p className="helper-error">
                                        {
                                          errors.extendDates[index]?.endDate
                                            ?.message
                                        }
                                      </p>
                                    )
                                  }
                                  onChange={(val: any) => {
                                    if (!val) return;

                                    const extendFields =
                                      watch('extendDates') || [];
                                    const prevValue = watch(
                                      `extendDates.${index}.endDate`,
                                    );

                                    const isValid = validateExtendDate(
                                      val,
                                      'endDate',
                                      index,
                                      setValue as (
                                        name: string,
                                        value: any,
                                        options?: any,
                                      ) => void,
                                      extendFields as unknown as Array<{
                                        startDate?: Dayjs | null;
                                        endDate?: Dayjs | null;
                                      }>,
                                    );

                                    if (isValid) {
                                      setValue(
                                        `extendDates.${index}.endDate`,
                                        val,
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      );
                                    } else {
                                      setValue(
                                        `extendDates.${index}.endDate`,
                                        prevValue,
                                        {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        },
                                      );
                                      setReRender(prev => prev + 1);
                                    }
                                  }}
                                />
                              </div>
                            </Grid>
                            {/* 
                        <TimePicker
                          control={control}
                          id={`extend-end-time-${index}`}
                          minutesStep={TIME_PICKER_MINUTES_STEP}
                          label="End Time"
                          name={`extendDates.${index}.endTime`}
                          placeholder="Select End Time"
                          error={!!errors?.extendDates?.[index]?.endTime}
                          requiredField
                          helperText={
                            errors?.extendDates?.[index]?.endTime?.message || ''
                          }
                          onChange={(val: any) => {
                            setValue(`extendDates.${index}.endTime`, val);
                          }}
                        /> */}

                            <Grid item sm={1} xs={2}>
                              {extendDateFields.length >= 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => removeExtendDate(index)}
                                  sx={{ color: 'error.main' }}
                                  className="mt36"
                                >
                                  <i className="delete-red-icon"></i>
                                </IconButton>
                              )}
                            </Grid>
                          </Grid>
                        </>
                      ))}

                      {extendDateFields.length >= 1 && (
                        <div className="text-right">
                          <Button
                            className="mt-12"
                            variant="text"
                            // startIcon={<AddIcon />}
                            onClick={() => {
                              const lastEnd =
                                watchedExtendDates?.at(-1)?.endDate;
                              if (!lastEnd) {
                                notify(
                                  'Please set end date for all extension periods before adding new ones.',
                                  'error',
                                );
                                return;
                              }
                              appendExtendDate({
                                startDate: dayjs(
                                  watchedExtendDates?.at(-1)?.endDate as any,
                                ).add(1, 'day'),
                                endDate: undefined as any,
                              });
                              void trigger('extendDates');
                            }}
                          >
                            <u>+ Add Range</u>
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Extend Service Actions */}
                    {/* <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleBackToEdit}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setIsExtendServiceMode(false);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Extend Service
                    </Button>
                  </Box> */}
                  </>
                )}

                {/* Notes Section */}
                <div className="form-in ">
                  <label className="mb-8 d-block">Notes</label>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="Add Notes here..."
                        variant="outlined"
                        error={!!errors.notes}
                        helperText={errors.notes?.message}
                        onChange={e => {
                          const value = e.target.value.slice(0, 1000); // limit only
                          field.onChange(value); // allow spaces
                        }}
                      />
                    )}
                  />
                </div>
                <p className="text-right mb-12 mt-8">
                  {watch('notes')?.length ?? 0}/1000
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <div>
                <Button
                  onClick={() => setWarningOpen(true)}
                  variant="outlined"
                  sx={{ textTransform: 'none' }}
                  size="medium"
                  className="min_width_btn_160"
                >
                  Cancel
                </Button>
              </div>
              <div>
                <Button
                  type="submit"
                  disabled={isSubmitting || !isValid}
                  variant="contained"
                  size="medium"
                  className="min_width_btn_160"
                  // disabled={!isValid}
                  sx={{ textTransform: 'none' }}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <WarningDialog
        open={warningOpen}
        title="Discard Changes"
        description="Cancelling will discard your edits. Do you want to continue?"
        type="warning"
        handleClose={() => setWarningOpen(false)}
        onClick={handleClose}
        cancelBtnText="Cancel"
        confirmBtnText="Yes, I'm sure"
      />
    </Dialog>
  );
};

export default EditShiftModal;
